import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailsDrawer } from "@/components/details-drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface Dto { nextNumberType: string; description: string; value: number; idPrefix: string; usedBy: string[]; }

export function NextNumbersPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["next-numbers"], queryFn: () => apiFetch<Dto[]>("/api/next-numbers") });
  const [editing, setEditing] = useState<Dto | null>(null);

  const patch = useMutation({
    mutationFn: (req: { type: string; value: number; idPrefix: string; confirmCode: string }) =>
      apiFetch(`/api/next-numbers/${encodeURIComponent(req.type)}`,
        { method: "PATCH", body: JSON.stringify({ value: req.value, idPrefix: req.idPrefix, confirmCode: req.confirmCode }) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["next-numbers"] }); setEditing(null); toast.success("Updated"); },
    onError: () => toast.error("Update failed — confirm code must equal the type"),
  });

  const cols: DataGridColumn<Dto>[] = [
    { id: "type",  header: "Type",   accessor: (r) => <button className="underline" onClick={() => setEditing(r)}>{r.nextNumberType}</button> },
    { id: "desc",  header: "Description", accessor: (r) => r.description },
    { id: "val",   header: "Next value", accessor: (r) => r.value, align: "right" },
    { id: "prefix",header: "ID prefix", accessor: (r) => r.idPrefix },
    { id: "used",  header: "Used by", accessor: (r) => r.usedBy.join(", ") || "—" },
  ];

  return (
    <ListPage title="Next-number sequences">
      <DataGrid rows={data ?? []} columns={cols} rowKey={(r) => r.nextNumberType}
        totalRows={data?.length ?? 0} pageIndex={0} pageSize={200} onPageChange={() => { /* fixed page */ }} />
      <DetailsDrawer open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }} title={`Edit ${editing?.nextNumberType ?? ""}`}>
        {editing && <EditForm row={editing} onSubmit={(v) => patch.mutate({ type: editing.nextNumberType, ...v })} pending={patch.isPending} />}
      </DetailsDrawer>
    </ListPage>
  );
}

function EditForm({ row, onSubmit, pending }: { row: Dto; onSubmit: (r: { value: number; idPrefix: string; confirmCode: string }) => void; pending: boolean }) {
  const [val, setVal] = useState(row.value);
  const [pfx, setPfx] = useState(row.idPrefix);
  const [confirmCode, setConfirm] = useState("");
  const decreasing = val < row.value;
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ value: val, idPrefix: pfx, confirmCode }); }} className="space-y-3">
      <div><Label htmlFor="v">Next value</Label><Input id="v" type="number" value={val} onChange={(e) => setVal(Number(e.target.value))} /></div>
      <div><Label htmlFor="p">ID prefix</Label><Input id="p" value={pfx} onChange={(e) => setPfx(e.target.value)} /></div>
      {decreasing && <p role="alert" className="text-sm text-destructive">Decreasing this number can collide with existing records.</p>}
      <div><Label htmlFor="cc">Type the sequence code <code>{row.nextNumberType}</code> to confirm</Label>
        <Input id="cc" value={confirmCode} onChange={(e) => setConfirm(e.target.value)} /></div>
      <Button type="submit" disabled={pending || confirmCode !== row.nextNumberType}>{pending ? "Saving…" : "Save"}</Button>
    </form>
  );
}
